import { zonedDate } from '../../../../utils/date-time';
import moment from 'moment';
import './ScheduleView.css';
import 'moment/locale/pt-br';
import ScheduleViewHeader from './ScheduleViewHeader';
import { baseURL } from '../../../../AxiosConfig';
import { Agendamento, StatusHorario } from '../../Enum';
import { ReactNode, useCallback, useEffect, useId, useState } from 'react';
import { useAppContext } from '../../../../contexts/AppContext';
import { Modal } from '../../../../components/Modal';
import { HorariosAtendenteService } from '../../Service';
import { FormEditSchedule } from './FormEditSchedule';
import { ClientHistory } from './ClientHistory';
import { ScheduleChanges } from './ScheduleChanges';
import { VendasService } from '../../../vendas/Service';
import { useOptionalAgendaContext } from '../ContextAgenda';
import { ExternalLink, MessageCircle } from 'lucide-react';

const horariosAtendenteService = new HorariosAtendenteService();
const vendasService = new VendasService();

moment.locale('pt-br');

const formatCurrency = (value: number) =>
    Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDateTime = (value: string | Date, timezone?: string) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '-';
    return zonedDate(date, timezone).format('DD/MM/YYYY HH:mm');
};

const getDiscountLabel = (type: string, value: number) => {
    if (!type || !Number(value)) return '';
    if (type === 'PERCENTUAL') return `${Number(value)}%`;
    if (type === 'VALOR') return formatCurrency(Number(value));
    return '';
};

const getItemsQuantity = (items: any[]) =>
    (items || []).reduce((sum, item) => sum + Number(item?.quantity || 0), 0);

const getSaleSourceLabel = (source?: string) => {
    if (source === 'RECORRENTE') return 'Recorrente';
    return 'Manual';
};

const recurrenceLabels: Record<string, string> = {
    dias: 'Dias',
    semanas: 'Semanas',
    meses: 'Meses',
};

const weekdayLabels: Record<string, string> = {
    domingo: 'domingo',
    segunda: 'segunda-feira',
    terca: 'terça-feira',
    quarta: 'quarta-feira',
    quinta: 'quinta-feira',
    sexta: 'sexta-feira',
    sabado: 'sábado',
};

const monthlyOccurrenceLabels: Record<string, string> = {
    primeira: 'Primeira',
    segunda: 'Segunda',
    terceira: 'Terceira',
    quarta: 'Quarta',
    ultima: 'Última',
};

const formatRecurrenceRule = (recurrence: any) => {
    const frequency = Number(recurrence?.frequencia || 1);
    const tipo = String(recurrence?.tipo || '');

    if (tipo === 'semanas') {
        const weekday = weekdayLabels[recurrence?.diaSemana] || recurrence?.diaSemana;
        return `A cada ${frequency} semana(s)${weekday ? `, ${weekday}` : ''}`;
    }

    if (tipo === 'meses') {
        if (recurrence?.modoMensal === 'diaSemana') {
            const occurrence = monthlyOccurrenceLabels[recurrence?.ocorrenciaMensal] || 'Ocorrência';
            const weekday = weekdayLabels[recurrence?.diaSemana] || 'dia da semana';
            return `${occurrence} ${weekday} do mês, a cada ${frequency} mês(es)`;
        }

        return `Dia ${recurrence?.diaMes || '-'} do mês, a cada ${frequency} mês(es)`;
    }

    if (tipo === 'dias') return `A cada ${frequency} dia(s)`;
    return 'Regra de recorrência';
};

const getEditablePriceValue = (schedule: Agendamento) => {
    const currentPrice = Number(schedule.totalPrice ?? schedule.service.price ?? 0);
    return currentPrice > 0 ? String(currentPrice) : '';
};

// Lista agrupada (inset grouped do iOS): titulo fora do card, linhas separadas por hairline.
function DetailGroup({
    title,
    accessory,
    footer,
    padded = false,
    className = '',
    children,
}: {
    title: string;
    accessory?: ReactNode;
    footer?: string;
    padded?: boolean;
    className?: string;
    children: ReactNode;
}) {
    return (
        <section className={`sf-detail-group-wrap ${className}`}>
            <div className="sf-detail-group-title">
                <h3>{title}</h3>
                {accessory}
            </div>
            <div className={`sf-detail-group ${padded ? 'is-padded' : ''}`}>{children}</div>
            {footer && <p className="sf-detail-group-footer">{footer}</p>}
        </section>
    );
}

function DetailRow({
    label,
    action,
    stacked = false,
    muted = false,
    children,
}: {
    label: ReactNode;
    action?: ReactNode;
    stacked?: boolean;
    muted?: boolean;
    children?: ReactNode;
}) {
    return (
        <div className={`sf-detail-row ${stacked ? 'is-stacked' : ''} ${muted ? 'is-muted' : ''}`}>
            <div className="sf-detail-row-label">{label}</div>
            {children !== undefined && <div className="sf-detail-row-value">{children}</div>}
            {action && <div className="sf-detail-row-action">{action}</div>}
        </div>
    );
}

function EditActions({ onCancel, onSave, saveLabel }: { onCancel: () => void; onSave: () => void; saveLabel: string }) {
    return (
        <div className="sf-detail-edit-actions">
            <button type="button" className="sf-detail-link is-muted" onClick={onCancel}>Cancelar</button>
            <button type="button" className="sf-detail-pill" onClick={onSave} aria-label={saveLabel}>Salvar</button>
        </div>
    );
}

export default function ScheduleView({ schedule }: { schedule: Agendamento }) {
    const [activeTab, setActiveTab] = useState('general');
    const tabsId = useId();
    const tabs = [
        { key: 'general', label: 'Geral' },
        { key: 'history', label: 'Histórico' },
        { key: 'sales', label: 'Vendas' },
        { key: 'changes', label: 'Alterações' },
    ];
    const agendaContext = useOptionalAgendaContext();
    const reloadSchedules = agendaContext?.reloadSchedules || (() => undefined);
    const [isEditingSchedule, setIsEditingSchedule] = useState(false);
    const [isEditingPrice, setIsEditingPrice] = useState(false);
    const [isEditingObservation, setIsEditingObservation] = useState(false);
    const [observation, setObservation] = useState(schedule.observation);
    const [isEditingObservationClient, setIsEditingObservationClient] = useState(false);
    const [observationClient, setObservationClient] = useState(schedule.observationClient);
    const [priceInput, setPriceInput] = useState(() => getEditablePriceValue(schedule));
    const [recorrentesVenda, setRecorrentesVenda] = useState<any[]>([]);
    const [loadingRecorrentesVenda, setLoadingRecorrentesVenda] = useState(false);
    const [clientSales, setClientSales] = useState<any[]>([]);
    const [loadingClientSales, setLoadingClientSales] = useState(false);
    const [saleTarget, setSaleTarget] = useState<any | null>(null);
    const [saleLoadingId, setSaleLoadingId] = useState<string | null>(null);
    const { successSnackbar, errorSnackbar } = useAppContext();
    const sub: any = (schedule as any)?.subscription;
    const hasSub = !!sub;
    const isPreLinked = Boolean(sub?.isPreLinked);
    const recurrence = (schedule as any)?.recurrence;
    const hasRecurrence = Boolean(recurrence || schedule?.recorrenciaId);
    const subscriptionDiscountPercent = Number.isFinite(
        Number(sub?.appointmentDiscountPercent),
    )
        ? Number(sub?.appointmentDiscountPercent)
        : 100;
    const subscriptionUsages = Array.isArray(sub?.usages) && sub.usages.length
        ? sub.usages
        : sub?.usage
            ? [sub.usage]
            : [];
    const canEditPrice = ![StatusHorario.CANCELADO].includes(schedule.status);
    const extrasNames = schedule.servicosExtras?.map((extra) => extra.name).join(', ') || '';
    const googleCalendarLink = schedule?.googleEventLink;

    const loadRecorrentesVenda = useCallback(async () => {
        const clientId = String(schedule?.client?.uid || '').trim();
        if (!clientId) {
            setRecorrentesVenda([]);
            return;
        }

        try {
            setLoadingRecorrentesVenda(true);
            const { data } = await vendasService.findRecorrentes({
                status: 'ATIVA',
                clientId,
                page: 1,
                limit: 50,
            });
            setRecorrentesVenda(Array.isArray(data?.data) ? data.data : []);
        } catch (error) {
            console.error(error);
            setRecorrentesVenda([]);
        } finally {
            setLoadingRecorrentesVenda(false);
        }
    }, [schedule?.client?.uid]);

    const loadClientSales = useCallback(async () => {
        const clientId = String(schedule?.client?.uid || '').trim();
        if (!clientId) {
            setClientSales([]);
            return;
        }

        try {
            setLoadingClientSales(true);
            const { data } = await vendasService.findAll({
                clientId,
                page: 1,
                limit: 50,
            });
            setClientSales(Array.isArray(data?.data) ? data.data : []);
        } catch (error) {
            console.error(error);
            setClientSales([]);
        } finally {
            setLoadingClientSales(false);
        }
    }, [schedule?.client?.uid]);

    useEffect(() => {
        loadRecorrentesVenda();
        loadClientSales();
    }, [loadClientSales, loadRecorrentesVenda]);

    useEffect(() => {
        setSaleTarget(null);
        setSaleLoadingId(null);
    }, [schedule?._id]);

    useEffect(() => {
        setPriceInput(getEditablePriceValue(schedule));
    }, [schedule?._id, schedule?.totalPrice, schedule?.service?.price]);

    const handleCreateSaleFromRecorrente = useCallback(async (recorrente: any) => {
        try {
            setSaleLoadingId(recorrente?._id || null);
            await vendasService.createByRecorrente(recorrente._id);
            successSnackbar('Venda registrada a partir da recorrência.');
            setSaleTarget(null);
            await Promise.all([loadRecorrentesVenda(), loadClientSales(), reloadSchedules()]);
        } catch (error: any) {
            console.error(error);
            errorSnackbar(error?.response?.data?.message || 'Erro ao gerar venda pela recorrência.');
        } finally {
            setSaleLoadingId(null);
        }
    }, [errorSnackbar, loadClientSales, loadRecorrentesVenda, reloadSchedules, successSnackbar]);

    const handleUpdatePrice = async () => {
        if (!canEditPrice) {
            errorSnackbar(
                'Agendamento cancelado não permite alteração de preço.',
            );
            setIsEditingPrice(false);
            return;
        }

        const parsedPrice = priceInput.trim() === '' ? 0 : Number(priceInput);
        if (!Number.isFinite(parsedPrice) || parsedPrice < 0) {
            errorSnackbar('Informe um valor válido para o agendamento.');
            return;
        }

        try {
            await horariosAtendenteService.updatePrice(schedule._id, parsedPrice);
            successSnackbar('Preço atualizado com sucesso!');
            schedule.totalPrice = parsedPrice;
            setPriceInput(getEditablePriceValue({
                ...schedule,
                totalPrice: parsedPrice,
            }));
            setIsEditingPrice(false);
        } catch (error) {
            errorSnackbar('Erro ao alterar preço do agendamento.');
            console.error(error);
        }
    };

    const handleUpdateObservationClient = async () => {
        try {
            await horariosAtendenteService.updateObservationClient(schedule._id, observationClient || '');
            successSnackbar('Observação do cliente atualizada com sucesso!');
            schedule.observationClient = observationClient;
            setIsEditingObservationClient(false);
        } catch (error) {
            errorSnackbar('Erro ao alterar observação do cliente do agendamento.');
            console.error(error);
        }
    };

    const formatBRL = (n?: number) =>
        new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(n || 0));


    const handleUpdateObservation = async () => {
        try {
            await horariosAtendenteService.updateObservation(schedule._id, observation || '');
            successSnackbar('Observação atualizada com sucesso!');
            schedule.observation = observation;
            setIsEditingObservation(false);
        } catch (error) {
            errorSnackbar('Erro ao alterar observação do agendamento.');
            console.error(error);
        }
    };

    const isCancelled = [StatusHorario.CANCELADO].includes(schedule.status);
    const priceLabel = formatCurrency(Number(schedule?.totalPrice ?? schedule?.service?.price ?? 0));
    const scheduledAtCell = zonedDate(schedule?.createdAt, schedule.timezone).format('DD/MM/YYYY HH:mm');

    const attendantValue = (
        <span className="sf-detail-person">
            <img
                alt=""
                src={`${baseURL}/users/profileImage/${schedule?.attendant.uid}`}
                className="sf-detail-person-avatar"
                loading="lazy"
            />
            <span className="truncate">{schedule?.attendant.name}</span>
            {schedule?.attendant?.phoneNumber ? (
                <a href={`https://wa.me/${schedule.attendant.phoneNumber.replace(/\D/g, '')}`}
                    target="_blank" rel="noopener noreferrer" className="sf-detail-icon-link"
                    aria-label="Conversar com o atendente no WhatsApp" title="WhatsApp do atendente">
                    <MessageCircle aria-hidden="true" />
                </a>
            ) : null}
        </span>
    );

    const priceRow = isEditingPrice && canEditPrice ? (
        <DetailRow label="Valor" stacked>
            <div className="sf-detail-edit">
                <label className="sf-detail-currency-input">
                    <span aria-hidden="true">R$</span>
                    <input
                        type="number"
                        aria-label="Valor do atendimento" value={priceInput}
                        onChange={(e) => setPriceInput(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleUpdatePrice();
                        }}
                        min="0"
                        step="0.01"
                        placeholder="0,00"
                        autoFocus
                    />
                </label>
                <EditActions
                    onCancel={() => {
                        setPriceInput(getEditablePriceValue(schedule));
                        setIsEditingPrice(false);
                    }}
                    onSave={handleUpdatePrice}
                    saveLabel="Salvar valor"
                />
            </div>
        </DetailRow>
    ) : (
        <DetailRow
            label="Valor"
            action={
                <button
                    type="button"
                    className="sf-detail-link"
                    aria-label="Editar valor" disabled={!canEditPrice}
                    onClick={() => {
                        setPriceInput(getEditablePriceValue(schedule));
                        setIsEditingPrice(true);
                    }}
                >
                    Editar
                </button>
            }
        >
            <span className="sf-detail-amount">{priceLabel}</span>
        </DetailRow>
    );

    const renderObservationRow = ({
        label,
        value,
        editing,
        draft,
        setDraft,
        setEditing,
        onSave,
        ariaLabel,
    }: {
        label: string;
        value?: string;
        editing: boolean;
        draft?: string;
        setDraft: (value: string) => void;
        setEditing: (value: boolean) => void;
        onSave: () => void;
        ariaLabel: string;
    }) => (
        <DetailRow
            label={label}
            stacked
            action={!editing && (
                <button
                    type="button"
                    className="sf-detail-link"
                    disabled={isCancelled}
                    aria-label={`Editar ${ariaLabel.toLowerCase()}`}
                    onClick={() => {
                        setDraft(value || '');
                        setEditing(true);
                    }}
                >
                    Editar
                </button>
            )}
        >
            {editing ? (
                <div className="sf-detail-edit">
                    <textarea
                        aria-label={ariaLabel} value={draft || ''}
                        onChange={(e) => setDraft(e.target.value)}
                        className="textarea textarea-sm w-full"
                        autoFocus
                    />
                    <EditActions onCancel={() => setEditing(false)} onSave={onSave} saveLabel={`Salvar ${ariaLabel.toLowerCase()}`} />
                </div>
            ) : (
                value ? <p className="sf-detail-note">{value}</p> : <span className="sf-detail-muted">Nenhuma observação</span>
            )}
        </DetailRow>
    );

    const recurringSalesContent = (
        <>
            {loadingRecorrentesVenda && (
                <DetailRow label="Carregando recorrências..." muted />
            )}

            {!loadingRecorrentesVenda && recorrentesVenda.length === 0 && (
                <DetailRow label="Cliente sem recorrência de venda ativa." muted />
            )}

            {!loadingRecorrentesVenda && recorrentesVenda.length > 0 && recorrentesVenda.map((recorrente) => (
                <DetailRow
                    key={recorrente?._id}
                    label={
                        <span className="flex flex-col">
                            <span className="sf-detail-amount">{formatCurrency(Number(recorrente?.recurringTotal || 0))}</span>
                            <span className="sf-detail-row-hint">
                                {Number(recorrente?.items?.length || 0)} item(ns)
                                {recorrente?.lastSaleAt
                                    ? ` · Última venda ${formatDateTime(recorrente.lastSaleAt, schedule.timezone)}`
                                    : ' · Sem venda registrada'}
                            </span>
                        </span>
                    }
                    action={
                        <button
                            type="button"
                            className="sf-detail-pill"
                            disabled={saleLoadingId === recorrente?._id}
                            onClick={() => setSaleTarget(recorrente)}
                        >
                            {saleLoadingId === recorrente?._id ? 'Gerando...' : 'Gerar venda'}
                        </button>
                    }
                />
            ))}
        </>
    );

    const salesTabContent = (
        <DetailGroup title="Vendas do cliente" padded>
            {loadingClientSales && (
                <div className="sf-detail-empty" role="status">Carregando vendas do cliente...</div>
            )}

            {!loadingClientSales && clientSales.length === 0 && (
                <div className="sf-detail-empty">Cliente sem vendas registradas.</div>
            )}

            {!loadingClientSales && clientSales.length > 0 && (
                <div className="overflow-x-auto">
                    <table className="table table-compact">
                        <thead>
                        <tr>
                            <th>Data</th>
                            <th>Origem</th>
                            <th>Itens</th>
                            <th className="text-right">Total</th>
                        </tr>
                        </thead>
                        <tbody>
                        {clientSales.map((sale) => (
                            <tr key={sale?._id}>
                                <td>{formatDateTime(sale?.createdAt, schedule.timezone)}</td>
                                <td>{getSaleSourceLabel(sale?.source)}</td>
                                <td>
                                    {(sale?.items?.length || 0)} tipo(s) · {getItemsQuantity(sale?.items || [])} unidade(s)
                                </td>
                                <td className="text-right font-semibold">{formatCurrency(Number(sale?.total || 0))}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>
                </div>
            )}
        </DetailGroup>
    );

    return (
        <div className="sf-schedule-detail">
            {schedule && (
                <ScheduleViewHeader
                    schedule={schedule}
                    onEditToggle={() => setIsEditingSchedule(!isEditingSchedule)}
                />

            )}

            {!!saleTarget && (
                <Modal
                    title="Confirmar venda pela recorrência"
                    panelClassName="sf-schedule-detail-panel"
                    onClose={() => {
                        if (saleLoadingId === saleTarget?._id) return;
                        setSaleTarget(null);
                    }}
                >
                    <div className="flex flex-col gap-4">
                        <div className="text-sm">
                            Cliente: <span className="font-semibold">{schedule?.client?.name || 'Não informado'}</span>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th>Item</th>
                                        <th className="text-right">Qtd.</th>
                                        <th className="text-right">Preço</th>
                                        <th className="text-right">Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(saleTarget?.items || []).map((item: any, index: number) => {
                                        const quantity = Number(item?.quantity || 0);
                                        const unitPrice = Number(item?.unitPrice || 0);
                                        const lineTotal = quantity * unitPrice;
                                        return (
                                            <tr key={`${item?.itemId || 'item'}-${index}`}>
                                                <td>{item?.name || '-'}</td>
                                                <td className="text-right">{quantity}</td>
                                                <td className="text-right">{formatCurrency(unitPrice)}</td>
                                                <td className="text-right">{formatCurrency(lineTotal)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div className="flex flex-col gap-1 text-sm">
                            <div className="flex items-center justify-between">
                                <span>Subtotal</span>
                                <span>{formatCurrency(Number(saleTarget?.recurringSubTotal || 0))}</span>
                            </div>

                            {Number(saleTarget?.recurringDiscountAmount || 0) > 0 && (
                                <div className="flex items-center justify-between">
                                    <span>
                                        Desconto
                                        {getDiscountLabel(saleTarget?.discountType, saleTarget?.discountValue)
                                            ? ` (${getDiscountLabel(saleTarget?.discountType, saleTarget?.discountValue)})`
                                            : ''}
                                    </span>
                                    <span>- {formatCurrency(Number(saleTarget?.recurringDiscountAmount || 0))}</span>
                                </div>
                            )}

                            <div className="flex items-center justify-between text-base font-semibold">
                                <span>Total</span>
                                <span>{formatCurrency(Number(saleTarget?.recurringTotal || 0))}</span>
                            </div>
                        </div>

                        <div className="text-sm">Ao confirmar, a venda será registrada e o estoque será baixado.</div>

                        <div className="flex justify-end gap-2">
                            <button
                                className="btn btn-ghost"
                                disabled={saleLoadingId === saleTarget?._id}
                                onClick={() => setSaleTarget(null)}
                            >
                                Voltar
                            </button>
                            <button
                                className="btn btn-primary"
                                disabled={saleLoadingId === saleTarget?._id}
                                onClick={() => handleCreateSaleFromRecorrente(saleTarget)}
                            >
                                {saleLoadingId === saleTarget?._id ? 'Confirmando...' : 'Confirmar venda'}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

            {isEditingSchedule
                ? <FormEditSchedule
                    schedule={schedule}
                    onEditToggle={() => setIsEditingSchedule(!isEditingSchedule)}
                />
                : <>
                    {/* Controle segmentado (iOS): trilho neutro e segmento ativo em superficie elevada. */}
                    <div className="sf-detail-tabs" role="tablist" aria-label="Detalhes do agendamento">
                        {tabs.map((tab, index) => (
                            <button key={tab.key} id={`${tabsId}-${tab.key}-tab`} type="button" role="tab"
                                aria-selected={activeTab === tab.key} aria-controls={`${tabsId}-${tab.key}-panel`}
                                tabIndex={activeTab === tab.key ? 0 : -1}
                                onClick={() => setActiveTab(tab.key)}
                                onKeyDown={(event) => {
                                    let next = index;
                                    if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
                                    else if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
                                    else if (event.key === 'Home') next = 0;
                                    else if (event.key === 'End') next = tabs.length - 1;
                                    else return;
                                    event.preventDefault();
                                    setActiveTab(tabs[next].key);
                                    document.getElementById(`${tabsId}-${tabs[next].key}-tab`)?.focus();
                                }}>
                                {tab.label}
                            </button>
                        ))}
                    </div>

                    <div id={`${tabsId}-general-panel`} role="tabpanel" aria-labelledby={`${tabsId}-general-tab`}
                        hidden={activeTab !== 'general'} tabIndex={0}>
                        <DetailGroup title="Atendimento">
                            <DetailRow label="Profissional">{attendantValue}</DetailRow>
                            <DetailRow label="Serviço">{schedule?.service.name}</DetailRow>
                            {!!extrasNames && <DetailRow label="Extras">{extrasNames}</DetailRow>}
                            {priceRow}
                            <DetailRow label="Agendado em">{scheduledAtCell}</DetailRow>
                            {googleCalendarLink && (
                                <DetailRow label="Google Agenda">
                                    <a href={googleCalendarLink} target="_blank" rel="noopener noreferrer" className="sf-detail-link">
                                        Abrir <ExternalLink className="text-[10px]" aria-hidden="true" />
                                    </a>
                                </DetailRow>
                            )}
                        </DetailGroup>

                        <DetailGroup title="Observações">
                            {renderObservationRow({
                                label: 'Do cliente',
                                value: schedule?.observationClient,
                                editing: isEditingObservationClient,
                                draft: observationClient,
                                setDraft: setObservationClient,
                                setEditing: setIsEditingObservationClient,
                                onSave: handleUpdateObservationClient,
                                ariaLabel: 'Observação do cliente',
                            })}
                            {renderObservationRow({
                                label: 'Do atendimento',
                                value: schedule?.observation,
                                editing: isEditingObservation,
                                draft: observation,
                                setDraft: setObservation,
                                setEditing: setIsEditingObservation,
                                onSave: handleUpdateObservation,
                                ariaLabel: 'Observação do atendimento',
                            })}
                            {!!schedule?.cancellation?.date && (
                                <DetailRow label="Cancelamento" stacked>
                                    {schedule.cancellation.observation && <p className="sf-detail-note">{schedule.cancellation.observation}</p>}
                                    <p className="sf-detail-row-hint">{zonedDate(schedule.cancellation.date, schedule.timezone).format('DD/MM/YYYY HH:mm')}</p>
                                </DetailRow>
                            )}
                        </DetailGroup>

                        {hasSub && (
                            <DetailGroup
                                title="Assinatura"
                                accessory={isPreLinked ? <span className="sf-detail-tag is-warning">Pré-vinculada</span> : undefined}
                            >
                                <DetailRow label="Plano"><span className="font-medium">{sub.planName || 'Plano'}</span></DetailRow>
                                <DetailRow label="Cobrança">
                                    {sub.billingType === 'UNICO'
                                        ? 'Pagamento único'
                                        : (sub.recurrence
                                            ? `${sub.recurrence.value} ${sub.recurrence.unit}(s)`
                                            : 'Recorrente')}
                                    {' · '}
                                    {formatBRL(sub.price)}
                                </DetailRow>
                                <DetailRow label="Desconto">{subscriptionDiscountPercent.toLocaleString('pt-BR')}%</DetailRow>
                                <DetailRow label={isPreLinked ? 'Período previsto' : 'Período'}>
                                    {sub?.period?.inicio && sub?.period?.fim
                                        ? `${zonedDate(sub?.period?.inicio, schedule.timezone).format('DD/MM/YYYY')} – ${zonedDate(sub?.period?.fim, schedule.timezone).format('DD/MM/YYYY')}`
                                        : 'Aguardando período'}
                                </DetailRow>
                                <DetailRow label="Vencimento">
                                    {sub?.period?.dueAt
                                        ? zonedDate(sub.period.dueAt, schedule.timezone).format('DD/MM/YYYY')
                                        : isPreLinked
                                            ? 'Aguardando período'
                                            : '—'}
                                    {sub?.period?.cobrancaStatus && (
                                        <span className="sf-detail-tag ml-2">{sub.period.cobrancaStatus}</span>
                                    )}
                                </DetailRow>
                                {subscriptionUsages.length ? (
                                    subscriptionUsages.map((usage: any) => (
                                        <DetailRow
                                            key={usage?.serviceId || usage?.serviceName}
                                            label={`Uso · ${usage?.serviceName || 'Serviço'}`}
                                        >
                                            <span className="sf-detail-amount">{(usage?.used ?? 0)} / {(usage?.limit ?? 0)}</span>
                                        </DetailRow>
                                    ))
                                ) : (
                                    <DetailRow label="Uso deste serviço"><span className="sf-detail-amount">0 / 0</span></DetailRow>
                                )}
                            </DetailGroup>
                        )}

                        {hasRecurrence && (
                            <DetailGroup title="Recorrência" accessory={<span className="sf-detail-tag">Ativa</span>}>
                                <DetailRow label="Repetição">
                                    {recurrence?.tipo ? recurrenceLabels[recurrence.tipo] || recurrence.tipo : 'Recorrência'}
                                </DetailRow>
                                <DetailRow label="Regra">
                                    {recurrence ? formatRecurrenceRule(recurrence) : 'Agendamento gerado por recorrência'}
                                </DetailRow>
                                <DetailRow label="Início">
                                    {recurrence?.startAt ? zonedDate(recurrence.startAt, schedule.timezone).format('DD/MM/YYYY') : '—'}
                                </DetailRow>
                                <DetailRow label="Fim">
                                    {recurrence?.endAt ? zonedDate(recurrence.endAt, schedule.timezone).format('DD/MM/YYYY') : '—'}
                                </DetailRow>
                                {recurrence?.assinaturaId && <DetailRow label="Vinculada à assinatura" muted />}
                            </DetailGroup>
                        )}

                        <DetailGroup title="Vendas recorrentes">
                            {recurringSalesContent}
                        </DetailGroup>
                    </div>
                    <div id={`${tabsId}-history-panel`} role="tabpanel" aria-labelledby={`${tabsId}-history-tab`}
                        hidden={activeTab !== 'history'} tabIndex={0}>
                        <ClientHistory
                            clientId={schedule.client.uid}
                            currentId={schedule._id}
                            attendantId={schedule.attendant.uid}
                            attendantName={schedule.attendant.name}
                            referenceDate={schedule.startAt}
                            active={activeTab === 'history'}
                        />
                    </div>
                    <div id={`${tabsId}-sales-panel`} role="tabpanel" aria-labelledby={`${tabsId}-sales-tab`}
                        hidden={activeTab !== 'sales'} tabIndex={0}>{salesTabContent}</div>
                    <div id={`${tabsId}-changes-panel`} role="tabpanel" aria-labelledby={`${tabsId}-changes-tab`}
                        hidden={activeTab !== 'changes'} tabIndex={0}>
                        <DetailGroup title="Alterações deste agendamento">
                            <ScheduleChanges scheduleId={schedule._id} active={activeTab === 'changes'} />
                        </DetailGroup>
                    </div>
                </>
            }
        </div>
    );
}
